import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SEO from "@/components/SEO";
import Breadcrumbs, { breadcrumbSchema } from "@/components/Breadcrumbs";
import { KineticLines, Reveal, SectionHead } from "@/components/Motion";
import { ProductCard } from "@/components/Cards";
import CTASection from "@/components/CTASection";
import NotFound from "@/pages/NotFound";
import api from "@/lib/api";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${slug}`).then((r) => setProject(r.data))
      .catch(() => setProject(null)).finally(() => setLoading(false));
  }, [slug]);

  if (!loading && !project) return <NotFound />;
  if (!project) return <div className="min-h-[60vh]" />;

  const crumbs = [{ label: "Projects", to: "/projects" }, { label: project.title }];
  const rows = [
    ["Industry", project.industry], ["Location", project.location], ["Application", project.application],
    ["Requirement", project.requirement], ["Solution", project.solution],
    ["Technical Details", project.technical_details], ["Outcome", project.outcome],
  ].filter(([, v]) => v);

  return (
    <div data-testid="project-detail-page">
      <SEO title={project.title} path={`/projects/${slug}`} description={project.application}
        image={project.image} jsonLd={breadcrumbSchema(crumbs)} />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Breadcrumbs dark items={crumbs} />
          <p className="eyebrow eyebrow-light">{project.status} — {project.industry}</p>
          <KineticLines lines={[project.title]}
            className="mt-5 max-w-4xl font-display text-3xl font-medium leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl" />
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-14 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="spotlight-img overflow-hidden border border-line">
              <img src={project.image} alt={project.title} className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <dl className="border-t border-line">
            {rows.map(([k, v], i) => (
              <div key={k} className="grid gap-2 border-b border-line py-5 sm:grid-cols-4" data-testid={`project-row-${i}`}>
                <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-steel">{k}</dt>
                <dd className="text-sm leading-relaxed text-ink sm:col-span-3">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {project.related_products?.length > 0 && (
        <section className="border-t border-line bg-mist">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
            <SectionHead eyebrow="Products Used" title="Equipment in This Application" />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {project.related_products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
      <CTASection />
    </div>
  );
}
